<script lang="ts">
  import { mloc } from "module/utils";
  import { getSetting, setSetting } from "module/settings";

  import {
    filterStore,
    toggleFilter,
    updateFilter,
  } from "module/store/FilterStore";
  import { FilterType } from "module/store/Filters";
  import { ModuleSetting } from "module/store/ModuleSettings";

  import SearchApp from "app/search/SearchApp.svelte";
  import FilterEditor from "./FilterEditor.svelte";
  import FilterList from "./FilterList.svelte";
  import FilterMetaHeader from "./FilterMetaHeader.svelte";
  import { deleteDialog, newDialog } from "./dialogs";
  import NiceButton from "../NiceButton.svelte";
  import { stores } from "module/store/SettingsStore";

  let { active = false } = $props();

  let selectedId: string | undefined = $state();

  let filter = $derived(selectedId && $filterStore[selectedId]);
  let defaultsType = $derived(stores[ModuleSetting.FILTERS_ADD_DEFAULT_TYPE]);
  let defaultsSubtype = $derived(
    stores[ModuleSetting.FILTERS_ADD_DEFAULT_SUBTYPE]
  );
  let defaultsPack = $derived(stores[ModuleSetting.FILTERS_ADD_DEFAULT_PACKS]);

  function toggleDefaults(
    setting:
      | ModuleSetting.FILTERS_ADD_DEFAULT_TYPE
      | ModuleSetting.FILTERS_ADD_DEFAULT_SUBTYPE
      | ModuleSetting.FILTERS_ADD_DEFAULT_PACKS
  ) {
    setSetting(setting, !getSetting(setting));
  }

  function selectFilter(event: CustomEvent) {
    const { id } = event.detail;
    selectedId = id;
  }

  function clone(evt: CustomEvent<string>) {
    newDialog($filterStore[evt.detail], (newId) => (selectedId = newId));
  }

  function toggle(evt: CustomEvent<string>) {
    toggleFilter(evt.detail);
  }

  function deleteId(evt: CustomEvent<string>) {
    deleteDialog($filterStore[evt.detail], () => {
      if (evt.detail === selectedId) selectedId = undefined;
    });
  }

  function onConfigChange(evt: CustomEvent) {
    if (!filter || filter.type === FilterType.Default) return;

    filter.filterConfig = evt.detail;
    updateFilter(filter);
  }

  function newFilter() {
    newDialog(undefined, (newId: string) => {
      selectedId = newId;
    });
  }

  function changeSubTitle(evt: CustomEvent) {
    if (filter && filter.type !== FilterType.Default)
      updateFilter({ ...filter, subTitle: evt.detail });
  }

  function changeTag(evt: CustomEvent) {
    if (filter && filter.type !== FilterType.Default)
      updateFilter({ ...filter, tag: evt.detail });
  }

  function changeRole(evt: CustomEvent) {
    if (filter && filter.type !== FilterType.Default)
      updateFilter({ ...filter, role: evt.detail });
  }
</script>

<div class:hidden={!active} class="filters-tab" role="tabpanel">
  {#if filter}
    <header>
      <h3 on:click={() => (selectedId = undefined)}>
        <i class="fas fa-chevron-left"></i>
        @{filter?.tag}
      </h3>
      <FilterMetaHeader
        {filter}
        on:clone={clone}
        on:toggle={toggle}
        on:delete={deleteId}
        on:subTitleChange={changeSubTitle}
        on:tagChange={changeTag}
        on:roleChange={changeRole}
      />
    </header>
    <section>
      <main>
        <FilterEditor
          filterConfig={filter.filterConfig}
          on:change={onConfigChange}
        />
      </main>
      <aside class="search-app-container">
        <span class="try-me">
          {mloc("FilterEditorTryMe")}
          <i class="fas fa-hand-point-down"></i>
        </span>
        <SearchApp {filter} embedded />
      </aside>
    </section>
  {/if}

  <div class="header notes" class:hidden={selectedId}>
    {mloc("FilterListHeader")}
    <p>{mloc("FilterListGlobalNotes")}</p>
  </div>

  <FilterList
    selected={selectedId}
    types={[FilterType.World, FilterType.Default]}
    on:selectFilter={selectFilter}
    on:clone={clone}
    on:toggle={toggle}
    on:delete={deleteId}
  />
  <menu class:hidden={selectedId}>
    <NiceButton round on:click={newFilter}>
      <i class="fas fa-plus"></i>
      {mloc("FilterListCreateFilter")}
    </NiceButton>
  </menu>
  <menu class:hidden={selectedId}>
    <div class="subtypes">
      {mloc("FilterListDefaultFilterSectionTitle")}
      <label>
        <input
          type="checkbox"
          name="qi.includeDefaultType"
          id="includeDefaultType"
          checked={$defaultsType}
          on:change={() =>
            toggleDefaults(ModuleSetting.FILTERS_ADD_DEFAULT_TYPE)}
        />
        Types
      </label>
      <label>
        <input
          type="checkbox"
          name="qi.includeDefaultSubtype"
          id="includeDefaultSubtype"
          checked={$defaultsSubtype}
          on:change={() =>
            toggleDefaults(ModuleSetting.FILTERS_ADD_DEFAULT_SUBTYPE)}
        />
        Subtypes
      </label>
      <label>
        <input
          type="checkbox"
          name="qi.includeDefaultPack"
          id="includeDefaultPack"
          checked={$defaultsPack}
          on:change={() =>
            toggleDefaults(ModuleSetting.FILTERS_ADD_DEFAULT_PACKS)}
        />
        Compendiums
      </label>
    </div>
  </menu>
</div>

<style>
  .header {
    margin: 0;
    padding: 0.6em;
    border-bottom: 2px solid #444;
    flex: auto;
  }

  label {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }

  .filters-tab {
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .hidden {
    display: none;
  }
  header {
    width: 100%;
    padding: 0.4em 0.7em;
    border-bottom: 2px solid #333;
    background: rgba(0, 0, 0, 0.1);
  }
  h3 {
    border: none;
    margin: 0;
    display: flex;
    align-items: center;
    padding: 0.1em 0;
    color: var(--color-text-primary);
    text-shadow: none;
    width: fit-content;
  }
  section {
    width: 100%;
    display: flex;
    overflow: auto;
    flex-grow: 1;
  }
  aside {
    padding: 0.4em;
    width: 350px;
    border-left: 1px solid rgba(0, 0, 0, 0.3);
    overflow: auto;
    flex-shrink: 0;
  }
  aside :global(.search-app) {
    border: 1px solid #493a50;
    border-radius: 6px;
  }
  main {
    flex-grow: 1;
    padding: 0.4em;
    min-width: 300px;
    max-width: calc(100% - 200px);
    display: flex;
    flex-direction: column;
  }

  .search-app-container {
    display: flex;
    flex-direction: column;
    justify-content: end;
  }

  .try-me {
    text-align: center;
    opacity: 0.9;
    padding: 0.5em;
  }
  menu {
    display: flex;
    margin: 0;
    padding: 0.2em 1em;
  }
  .subtypes {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
  }

  .fa-chevron-left {
    cursor: pointer;
    display: flex;
    align-items: center;
    width: 25px;
  }
</style>
